import { ReactNode } from 'react';
import { toast as reactToastify } from 'react-toastify';
import styled from 'styled-components';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import { Box } from '../components/UI-CHAKRA/Box';
import { ReactComponent as SucessIcon } from '../assets/images/icons/mevo/success.svg';
import { ReactComponent as AlertIcon } from '../assets/images/icons/mevo/alert-mono-color.svg';
import { Flex } from '../components/UI-CHAKRA/Flex';
import { Text } from '../components/UI-CHAKRA/Text';
import { usePrescriptionStore } from '../stores/prescriptionStore';

/**
 * Considerando o tempo de leitura de 80 palavras por minuto
 */
const SECONDS_PER_WORD = 0.8;

/**
 * Retorna o tempo de leitura em milisegundos
 * @param {string} message
 * @returns {number}
 */
export const calculateReadingTime = (message: string): number => {
  const timeInSeconds = message.split(' ').length * SECONDS_PER_WORD;

  return timeInSeconds * 1000;
};

export const StyledToastContainer = styled(ToastContainer).attrs({
  className: 'toast-container',
  toastClassName: 'toast',
  bodyClassName: 'body',
  hideProgressBar: true,
  autoClose: 2000,
  limit: 1,
})`
  .Toastify__toast {
    border-radius: 8px;
    background: #fbeae5;

    margin: 0;
    min-height: 49px;
  }

  .Toastify__toast--error {
    background: #ff6161;
    color: #fff;
    line-height: 16px;
    border-radius: 12px;
    font-size: 14px;
    font-family: Ambit-Regular, Inter, sans-serif;

    button[aria-label='close'] {
      border: 2px solid #fff;
      border-radius: 50px;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: center;
      width: 16px;
      height: 16px;
      padding: 2px;
    }
  }

  .Toastify__toast--success {
    background: #5cd17e;
    color: #fff;
    line-height: 16px;
    border-radius: 12px;
    font-size: 14px;
    font-family: Ambit-Regular, Inter, sans-serif;

    button[aria-label='close'] {
      border: 2px solid #fff;
      border-radius: 50px;
      color: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: center;
      width: 16px;

      height: 16px;
      padding: 2px;
    }
  }
`;

interface ToastContainerProps {
  children: ReactNode;
  icon: React.ReactNode;
}

function CustomToastContainer(props: ToastContainerProps) {
  return (
    <Flex data-cy={'toast'}>
      <Flex alignItems={'center'} m={'0 10px'}>
        {props.icon}
      </Flex>

      <Box>{props.children}</Box>
    </Flex>
  );
}

export function ErrorToast(props: { children: ReactNode }) {
  const validationCode = usePrescriptionStore((store) => store.validationCode());

  return (
    <CustomToastContainer icon={<AlertIcon />}>
      <>
        {props.children}

        <Text fontSize={'10px'} as={'span'} color={'white'} opacity={'0.7'} ml={'5px'} letterSpacing={'1.5px'}>
          {validationCode}
        </Text>
      </>
    </CustomToastContainer>
  );
}

export function SuccessToast(props: { children: ReactNode }) {
  return <CustomToastContainer icon={<SucessIcon />}>{props.children}</CustomToastContainer>;
}

export const toast = {
  error(message: string, options?: { autoClose?: number | false }) {
    const autoClose = options?.autoClose !== undefined ? options.autoClose : calculateReadingTime(message);
    reactToastify.error(<ErrorToast>{message}</ErrorToast>, {
      autoClose: autoClose === false ? false : Math.max(autoClose, 10000), // Mínimo de 10 segundos para erros
      icon: false,
    });
  },
  success(message: string) {
    reactToastify.success(<SuccessToast>{message}</SuccessToast>, {
      autoClose: calculateReadingTime(message),
      icon: false,
    });
  },
};
